import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { BaseComponent, DisturbanceSubscription, NotificationSettingsService, ShapeType } from '@ridango/core';
import { ModalController, ToastController } from '@ionic/angular';
import { ApplicationFacade, Route, Stop } from '@ridango/components';
import Env from '@ridango/core/env';
import { formatDate } from '@angular/common';
import { from } from 'rxjs';
import { concatMap, toArray } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
    selector: 'rwidget-subscriptions-confirm-modal',
    templateUrl: './subscriptions-confirm-modal.component.html',
    styleUrls: ['./subscriptions-confirm-modal.component.scss']
})
export class SubscriptionsConfirmModalComponent extends BaseComponent implements OnDestroy {
    @Input() selectedNotification: string;
    @Input() stopsRoutesData: { stops: Stop[]; routes: Route[] } | null;

    assetsPath$ = this.applicationFacade.assetsPath$;
    ShapeType = ShapeType;

    todayStr = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    allDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    notificationTitleKey: Record<string, string> = {
        stops: 'STOP_NOTIFICATIONS_TITLE',
        routes: 'ROUTE_NOTIFICATIONS_TITLE',
        search: 'SEARCH_NOTIFICATIONS_TITLE'
    };

    constructor(
        @Inject('ENV') public env: Env,
        private translate: TranslateService,
        private router: Router,
        private modalCtrl: ModalController,
        private toastCtrl: ToastController,
        private applicationFacade: ApplicationFacade,
        private notificationService: NotificationSettingsService
    ) {
        super();
    }

    async cancel(): Promise<void> {
        await this.modalCtrl.dismiss(null, 'cancel');
    }

    async saveSubscription() {
        if (!this.stopsRoutesData) {
            return;
        }

        const stopSubs: DisturbanceSubscription[] = this.stopsRoutesData.stops.map((stop) => ({
            stopId: stop.id,
            routeId: null,
            daysOfWeek: this.allDays,
            startedAt: this.todayStr,
            endedAt: null,
            active: true
        }));

        const routeSubs: DisturbanceSubscription[] = this.stopsRoutesData.routes.map((route) => ({
            stopId: null,
            routeId: route.id,
            daysOfWeek: this.allDays,
            startedAt: this.todayStr,
            endedAt: null,
            active: true
        }));

        const subscriptions = [...stopSubs, ...routeSubs];

        from(subscriptions)
            .pipe(
                concatMap((sub) => this.notificationService.addDisturbanceSubscription(sub)),
                toArray()
            )
            .subscribe({
                next: async () => {
                    await this.presentToast(this.translate.instant('SUBSCRIPTIONS_SAVED_SUCCESS'));
                    await this.modalCtrl.dismiss(null, 'confirm');
                    await this.router.navigate([this.translate.currentLang, 'account', 'edit-subscriptions']);
                },
                error: async () => {
                    await this.presentToast(this.translate.instant('error_codeless'));
                    await this.modalCtrl.dismiss(null, 'confirm');
                }
            });
    }

    async presentToast(message: string) {
        const toast = await this.toastCtrl.create({
            message: 'ⓘ ' + message,
            duration: 5000,
            position: 'top',
            cssClass: 'custom-toast',
            buttons: [{ icon: 'close-circle', role: 'cancel' }]
        });
        await toast.present();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
